import { GitHub } from "projen/lib/github";
import { AgentRuleBundle } from "../agent-config-options";
import { AGENT_RULE_SCOPE, AgentProcedure, AgentSkill } from "../types";
import { DEFAULT_BUILD_POLICY, ResolvedBuildPolicy } from "./build-policy";
import { checkClosingKeywordsProcedure } from "./closing-keywords";
import { renderTitlePrefixTypeBullets } from "./issue-type";
import { hasComponent } from "./utils";

/*******************************************************************************
 *
 * set-issue-type.sh — Assign a GitHub issue type in one step.
 *
 * Wraps the two-step GraphQL flow (lookup repo issue types, then call
 * updateIssueIssueType) so callers do not have to hand-write the inline
 * queries every time an issue is created.
 *
 ******************************************************************************/

const setIssueTypeProcedure: AgentProcedure = {
  name: "set-issue-type.sh",
  description:
    "Assign a GitHub issue type (Feature / Task / Epic / Bug / etc.) via the updateIssueIssueType GraphQL mutation in a single step",
  content: [
    "#!/usr/bin/env bash",
    "# set-issue-type.sh — Assign a GitHub issue type in one step.",
    "#",
    "# Wraps the two-step GraphQL flow used to set an issue's type:",
    "#   1. Look up the repo's issue type node IDs.",
    "#   2. Call the updateIssueIssueType mutation.",
    "#",
    "# Usage:",
    "#   .claude/procedures/set-issue-type.sh <issue-number> <type-name>",
    "#",
    "# Where <type-name> is the human-readable GitHub issue type name",
    "# (e.g. Feature, Task, Epic, Bug). Matching is case-insensitive.",
    "#",
    "# Exits 0 on success. Exits non-zero with a clear diagnostic on stderr",
    "# for any validation error or API failure.",
    "",
    "set -uo pipefail",
    "",
    "# ── helpers ──────────────────────────────────────────────────────────",
    "",
    "err() {",
    '  printf "set-issue-type.sh: %s\\n" "$*" >&2',
    "}",
    "",
    "usage() {",
    "  cat >&2 <<'USAGE'",
    "Usage: set-issue-type.sh <issue-number> <type-name>",
    "",
    "  <issue-number>  Positive integer GitHub issue number.",
    "  <type-name>     Human-readable issue type (Feature, Task, Epic, Bug,",
    "                  or any type defined at the org level). Case-insensitive.",
    "",
    "Examples:",
    "  set-issue-type.sh 123 Feature",
    "  set-issue-type.sh 456 bug",
    "USAGE",
    "}",
    "",
    "# ── argument validation ──────────────────────────────────────────────",
    "",
    "if [[ $# -ne 2 ]]; then",
    '  err "expected 2 arguments, got $#"',
    "  usage",
    "  exit 2",
    "fi",
    "",
    'issue_number="$1"',
    'type_name="$2"',
    "",
    'if ! [[ "$issue_number" =~ ^[1-9][0-9]*$ ]]; then',
    "  err \"issue number must be a positive integer (got: '$issue_number')\"",
    "  exit 2",
    "fi",
    "",
    'if [[ -z "${type_name// /}" ]]; then',
    '  err "type name must be non-empty"',
    "  exit 2",
    "fi",
    "",
    "# ── dependency checks ────────────────────────────────────────────────",
    "",
    "for cmd in gh jq; do",
    '  if ! command -v "$cmd" >/dev/null 2>&1; then',
    '    err "required command not found on PATH: $cmd"',
    "    exit 3",
    "  fi",
    "done",
    "",
    "# ── resolve repo owner/name ──────────────────────────────────────────",
    "",
    "repo_json=$(gh repo view --json owner,name 2>/dev/null)",
    'if [[ -z "$repo_json" ]]; then',
    "  err \"could not resolve owning repo via 'gh repo view' (not a GitHub repo or gh not authenticated?)\"",
    "  exit 4",
    "fi",
    "",
    "owner=$(echo \"$repo_json\" | jq -r '.owner.login // empty')",
    "name=$(echo \"$repo_json\" | jq -r '.name // empty')",
    "",
    'if [[ -z "$owner" || -z "$name" ]]; then',
    '  err "could not parse owner/name from gh repo view output"',
    "  exit 4",
    "fi",
    "",
    "# ── look up issue type node ID ───────────────────────────────────────",
    "",
    "types_json=$(gh api graphql \\",
    "  -f query='query($owner:String!,$repo:String!){repository(owner:$owner,name:$repo){issueTypes(first:50){nodes{id name}}}}' \\",
    '  -f owner="$owner" -f repo="$name" 2>/dev/null)',
    "",
    'if [[ -z "$types_json" ]]; then',
    '  err "failed to query repo issue types via GraphQL"',
    "  exit 5",
    "fi",
    "",
    "types_nodes=$(echo \"$types_json\" | jq -c '.data.repository.issueTypes.nodes // []')",
    "",
    'if [[ "$types_nodes" == "[]" || "$types_nodes" == "null" ]]; then',
    '  err "repo ${owner}/${name} has no issue types defined"',
    "  exit 5",
    "fi",
    "",
    "# Case-insensitive match by name.",
    'type_id=$(echo "$types_nodes" | jq -r --arg n "$type_name" \'',
    "  .[] | select((.name | ascii_downcase) == ($n | ascii_downcase)) | .id",
    "' | head -n 1)",
    "",
    'if [[ -z "$type_id" ]]; then',
    "  available=$(echo \"$types_nodes\" | jq -r '.[].name' | paste -sd ',' - | sed 's/,/, /g')",
    "  err \"type '${type_name}' not found in ${owner}/${name}. Available types: ${available}\"",
    "  exit 6",
    "fi",
    "",
    "# ── look up issue node ID ────────────────────────────────────────────",
    "",
    'issue_id=$(gh issue view "$issue_number" --json id -q \'.id\' 2>/dev/null || echo "")',
    "",
    'if [[ -z "$issue_id" ]]; then',
    '  err "issue #${issue_number} not found in ${owner}/${name}"',
    "  exit 7",
    "fi",
    "",
    "# ── apply the mutation ───────────────────────────────────────────────",
    "",
    "result=$(gh api graphql \\",
    "  -f query='mutation($issueId:ID!,$typeId:ID!){updateIssueIssueType(input:{issueId:$issueId,issueTypeId:$typeId}){issue{number issueType{name}}}}' \\",
    '  -f issueId="$issue_id" -f typeId="$type_id" 2>&1)',
    "mutation_exit=$?",
    "",
    "if [[ $mutation_exit -ne 0 ]]; then",
    '  err "updateIssueIssueType mutation failed: ${result}"',
    "  exit 8",
    "fi",
    "",
    "# Print the resulting {number, issueType: {name}} JSON line.",
    "echo \"$result\" | jq -c '.data.updateIssueIssueType.issue'",
  ].join("\n"),
};

/*******************************************************************************
 *
 * clean-merged-branches.sh — Report local branches whose content is fully on
 * the base branch (analysis-only; safe to invoke non-interactively).
 *
 * Uses content equality (`git diff --name-only` against the base branch) so
 * squash-merged branches are correctly identified — `git branch -d`'s
 * commit-graph reachability check does not catch squash merges because the
 * original branch commits are replaced on the base by a single new commit
 * with a different hash.
 *
 * The procedure NEVER deletes branches; it only classifies and reports. The
 * `/clean-merged-branches` skill wraps it with the confirmation prompt and
 * the deletion loop.
 *
 ******************************************************************************/

const cleanMergedBranchesProcedure: AgentProcedure = {
  name: "clean-merged-branches.sh",
  description:
    "Analyse local branches and classify each as MERGED, UNMERGED, EMPTY, or SKIP_WORKTREE against a base branch (default: main). Analysis-only — never deletes branches. Handles squash merges via content equality.",
  content: [
    "#!/usr/bin/env bash",
    "# clean-merged-branches.sh — Analyse local branches against a base.",
    "#",
    "# Reports each local branch (other than HEAD and the base branch) as",
    "# MERGED, UNMERGED, EMPTY, or SKIP_WORKTREE. MERGED means every file the",
    "# branch added or modified now matches the base — so the branch content",
    "# is fully on the base even when the merge was a squash.",
    "#",
    "# This procedure is analysis-only. It never runs `git branch -D`. The",
    "# /clean-merged-branches slash-command skill wraps it with the deletion",
    "# prompt; the procedure itself is safe to invoke non-interactively from",
    "# any agent.",
    "#",
    "# Usage:",
    "#   .claude/procedures/clean-merged-branches.sh             # default base: main",
    "#   .claude/procedures/clean-merged-branches.sh --base develop",
    "#",
    "# Output (one line per branch, stable format for grepping):",
    "#   MERGED       <branch>  files=<n>",
    "#   UNMERGED     <branch>  files=<n>  differs=<n>",
    "#   EMPTY        <branch>",
    "#   SKIP_WORKTREE <branch>  worktree=<path>",
    "#",
    "# Exit codes:",
    "#   0 — analysis completed (whether or not any MERGED branches were found)",
    "#   2 — argument error",
    "#   3 — required command not on PATH",
    "#   4 — not a git repository",
    "",
    "set -uo pipefail",
    "",
    "# ── helpers ──────────────────────────────────────────────────────────",
    "",
    "err() {",
    '  printf "clean-merged-branches.sh: %s\\n" "$*" >&2',
    "}",
    "",
    "usage() {",
    "  cat >&2 <<'USAGE'",
    "Usage: clean-merged-branches.sh [--base <name>]",
    "",
    "  --base <name>   Base branch to compare against (default: main; falls",
    "                  back to whatever origin/HEAD points at if main is",
    "                  not present locally).",
    "",
    "Reports MERGED / UNMERGED / EMPTY / SKIP_WORKTREE for every local",
    "branch other than HEAD and the base branch. Does not delete anything.",
    "USAGE",
    "}",
    "",
    "# ── argument parsing ─────────────────────────────────────────────────",
    "",
    'base=""',
    "while [[ $# -gt 0 ]]; do",
    '  case "$1" in',
    "    --base)",
    '      if [[ $# -lt 2 || -z "${2:-}" ]]; then',
    '        err "--base requires a value"',
    "        usage",
    "        exit 2",
    "      fi",
    '      base="$2"',
    "      shift 2",
    "      ;;",
    "    -h|--help)",
    "      usage",
    "      exit 0",
    "      ;;",
    "    *)",
    '      err "unknown argument: $1"',
    "      usage",
    "      exit 2",
    "      ;;",
    "  esac",
    "done",
    "",
    "# ── dependency checks ────────────────────────────────────────────────",
    "",
    "for cmd in git; do",
    '  if ! command -v "$cmd" >/dev/null 2>&1; then',
    '    err "required command not found on PATH: $cmd"',
    "    exit 3",
    "  fi",
    "done",
    "",
    "# ── repo + base resolution ───────────────────────────────────────────",
    "",
    "if ! git rev-parse --git-dir >/dev/null 2>&1; then",
    '  err "not inside a git repository"',
    "  exit 4",
    "fi",
    "",
    "# Default base = main if it exists locally, otherwise whatever",
    "# origin/HEAD points at (e.g. master, trunk, develop).",
    'if [[ -z "$base" ]]; then',
    '  if git show-ref --verify --quiet "refs/heads/main"; then',
    '    base="main"',
    "  else",
    "    origin_head=$(git symbolic-ref --short refs/remotes/origin/HEAD 2>/dev/null || true)",
    '    if [[ -n "$origin_head" ]]; then',
    '      base="${origin_head#origin/}"',
    "    else",
    '      base="main"',
    "    fi",
    "  fi",
    "fi",
    "",
    "# Verify the chosen base actually exists as a local branch.",
    'if ! git show-ref --verify --quiet "refs/heads/$base"; then',
    "  err \"base branch '$base' does not exist locally\"",
    "  exit 4",
    "fi",
    "",
    "# Refresh remote-tracking refs and prune deleted upstream branches",
    "# so the gone-upstream signal is fresh (best effort — never fatal).",
    "git fetch --prune origin >/dev/null 2>&1 || true",
    "",
    'current=$(git rev-parse --abbrev-ref HEAD 2>/dev/null || echo "")',
    "",
    "# ── worktree map (branch -> worktree path, excluding the current) ───",
    "",
    "# Use git worktree list --porcelain to detect any branch checked out in",
    "# a separate worktree. We must skip those because `git branch -D` would",
    "# refuse to delete a branch that is checked out elsewhere.",
    "#",
    "# We use parallel arrays (not an associative array) to stay compatible",
    "# with bash 3 — macOS still ships bash 3.2 as /bin/bash, and the script",
    "# is invoked via #!/usr/bin/env bash which picks whichever bash is",
    "# first on PATH. Lookups are O(n) but n is tiny (worktree count).",
    "worktree_branches=()",
    "worktree_paths=()",
    'cwd_top=$(git rev-parse --show-toplevel 2>/dev/null || echo "")',
    'wt_path=""',
    "while IFS= read -r line; do",
    '  case "$line" in',
    "    worktree\\ *)",
    '      wt_path="${line#worktree }"',
    "      ;;",
    "    branch\\ *)",
    '      wt_branch="${line#branch refs/heads/}"',
    "      # Skip the worktree that matches the current top-level — that one",
    "      # is the *current* checkout; skipping HEAD is handled separately.",
    '      if [[ -n "$wt_path" && "$wt_path" != "$cwd_top" ]]; then',
    '        worktree_branches+=("$wt_branch")',
    '        worktree_paths+=("$wt_path")',
    "      fi",
    '      wt_path=""',
    "      ;;",
    '    "")',
    '      wt_path=""',
    "      ;;",
    "  esac",
    "done < <(git worktree list --porcelain 2>/dev/null)",
    "",
    "# Echo the worktree path for the given branch, or empty string if the",
    "# branch is not checked out in another worktree.",
    "lookup_worktree() {",
    '  local needle="$1"',
    "  local i",
    '  for i in "${!worktree_branches[@]}"; do',
    '    if [[ "${worktree_branches[$i]}" == "$needle" ]]; then',
    "      printf '%s\\n' \"${worktree_paths[$i]}\"",
    "      return 0",
    "    fi",
    "  done",
    "  return 0",
    "}",
    "",
    "# ── walk local branches ──────────────────────────────────────────────",
    "",
    'echo "Analysing local branches against ${base}…"',
    "echo",
    "",
    "while IFS= read -r branch; do",
    '  [[ -z "$branch" ]] && continue',
    '  [[ "$branch" == "$base" ]] && continue',
    '  [[ "$branch" == "$current" ]] && continue',
    "",
    '  wt=$(lookup_worktree "$branch")',
    '  if [[ -n "$wt" ]]; then',
    '    printf \'SKIP_WORKTREE %s  worktree=%s\\n\' "$branch" "$wt"',
    "    continue",
    "  fi",
    "",
    '  mb=$(git merge-base "$base" "$branch" 2>/dev/null || true)',
    '  if [[ -z "$mb" ]]; then',
    "    # No common ancestor with base — treat as UNMERGED (don't risk it).",
    "    printf 'UNMERGED     %s  files=?  differs=?\\n' \"$branch\"",
    "    continue",
    "  fi",
    "",
    '  files=$(git diff --name-only "$mb" "$branch" 2>/dev/null || true)',
    '  if [[ -z "$files" ]]; then',
    "    printf 'EMPTY        %s\\n' \"$branch\"",
    "    continue",
    "  fi",
    "",
    "  # Count how many of those files still differ from the base today.",
    "  # We use NUL-delimited git output to survive filenames with spaces.",
    "  diff_count=0",
    "  while IFS= read -r -d '' f; do",
    "    diff_count=$((diff_count + 1))",
    '  done < <(git diff -z --name-only "$branch" "$base" -- $files 2>/dev/null)',
    "",
    "  total=$(echo \"$files\" | wc -l | tr -d ' ')",
    "",
    '  if [[ "$diff_count" -eq 0 ]]; then',
    '    printf \'MERGED       %s  files=%s\\n\' "$branch" "$total"',
    "  else",
    '    printf \'UNMERGED     %s  files=%s  differs=%s\\n\' "$branch" "$total" "$diff_count"',
    "  fi",
    "done < <(git branch --format='%(refname:short)')",
    "",
    "exit 0",
  ].join("\n"),
};

/*******************************************************************************
 *
 * /clean-merged-branches skill — wraps the procedure with the
 * report-confirm-delete UX.
 *
 ******************************************************************************/

const cleanMergedBranchesSkill: AgentSkill = {
  name: "clean-merged-branches",
  description:
    "Report every local branch as MERGED or UNMERGED against the base (handles squash merges via content equality), then prompt the user to force-delete the MERGED list. Defaults base to main; --base overrides. Skips current branch, base branch, and any branch checked out in another worktree.",
  disableModelInvocation: true,
  userInvocable: true,
  platforms: { cursor: { exclude: true } },
  instructions: [
    "# Clean Merged Branches",
    "",
    "Identify local branches whose content is fully on the base branch —",
    "including branches that were squash-merged (where `git branch -d`",
    "refuses because the branch commits are not ancestors of the base) —",
    "report them to the user, and force-delete them after explicit",
    "confirmation.",
    "",
    "## Usage",
    "",
    "```",
    "/clean-merged-branches                # default base: main",
    "/clean-merged-branches --base develop # custom base branch",
    "```",
    "",
    "### Flags",
    "",
    "- **`--base <name>`** — base branch to compare against. Defaults to",
    "  `main`; falls back to whatever `origin/HEAD` points at if `main`",
    "  does not exist locally.",
    "",
    "## What This Skill Does",
    "",
    "1. **Analyse.** Runs `.claude/procedures/clean-merged-branches.sh`",
    "   (passing through any `--base` flag). The procedure walks every",
    "   local branch and prints one stable log line per branch:",
    "   - `MERGED       <branch>  files=<n>` — every file the branch added",
    "     or modified now matches the base. Safe to delete.",
    "   - `UNMERGED     <branch>  files=<n>  differs=<n>` — branch content",
    "     still differs from the base. Do NOT delete.",
    "   - `EMPTY        <branch>` — branch has no file changes vs. its",
    "     merge-base. Safe to delete.",
    "   - `SKIP_WORKTREE <branch>  worktree=<path>` — branch is checked",
    "     out in another worktree. Skipped (cannot be deleted while it's",
    "     in use).",
    "",
    "2. **Confirm.** If the report turns up at least one `MERGED` (or",
    "   `EMPTY`) branch, print the exact deletion list and a single",
    "   `[y/N]` prompt. Empty input defaults to **no** and aborts.",
    "",
    "3. **Delete.** On explicit `y` / `Y`, run `git branch -D <branch>`",
    "   for each confirmed branch and emit one `DELETED <branch>` line",
    "   per success. On any other answer (including empty input),",
    "   abort with `Aborted. No branches deleted.` and exit cleanly.",
    "",
    "If the report finds zero deletable branches, exit cleanly without",
    "prompting — there is nothing to confirm.",
    "",
    "## Behaviour Guarantees",
    "",
    "- **Always skip the current branch and the base branch.** The",
    "  procedure enforces this; the skill never needs to filter again.",
    "- **Never delete without an explicit `y`.** The default on empty",
    "  input is no. The skill never runs `git branch -D` until after",
    "  the prompt returns `y` or `Y`.",
    "- **Content equality is the proof.** A branch classifies as",
    "  `MERGED` when every file it added or modified matches the base",
    "  today — handles squash merges, file deletions, and renames.",
    "  The `[origin/<branch>: gone]` indicator is a useful secondary",
    "  signal but is NOT the deletion gate.",
    "- **Safe across worktrees.** Branches checked out in another",
    "  worktree are logged as `SKIP_WORKTREE` and excluded from the",
    "  deletion list.",
    "",
    "## Output",
    "",
    "The procedure's per-branch log lines first, then (if any deletable",
    "branches exist) the confirmation prompt, then one `DELETED <branch>`",
    "line per successful deletion or `Aborted. No branches deleted.`",
    "on abort.",
    "",
    "## Implementation Recipe",
    "",
    "```bash",
    "# 1. Run the analysis-only procedure and capture its output.",
    'report=$(.claude/procedures/clean-merged-branches.sh "$@")',
    'printf "%s\\n" "$report"',
    "",
    "# 2. Extract the MERGED + EMPTY branch names from the report.",
    "mergeable=()",
    "while IFS= read -r line; do",
    '  case "$line" in',
    '    "MERGED "*|"EMPTY "*)',
    "      # The branch name is the second whitespace-delimited token.",
    "      branch=$(echo \"$line\" | awk '{print $2}')",
    '      [[ -n "$branch" ]] && mergeable+=("$branch")',
    "      ;;",
    "  esac",
    'done <<< "$report"',
    "",
    "# 3. If nothing to delete, exit cleanly without prompting.",
    "if [[ ${#mergeable[@]} -eq 0 ]]; then",
    "  echo",
    '  echo "No merged branches to delete."',
    "  exit 0",
    "fi",
    "",
    "# 4. Show the list and prompt the user once.",
    "echo",
    'echo "The following ${#mergeable[@]} branches are safe to delete:"',
    "printf '  %s\\n' \"${mergeable[@]}\"",
    "echo",
    'read -r -p "Force-delete all ${#mergeable[@]} with git branch -D? [y/N] " answer',
    "",
    "# 5. On explicit y/Y only, delete each branch.",
    'if [[ "$answer" == "y" || "$answer" == "Y" ]]; then',
    '  for b in "${mergeable[@]}"; do',
    '    if git branch -D "$b" >/dev/null 2>&1; then',
    '      echo "DELETED      $b"',
    "    else",
    '      echo "DELETE_FAILED $b" >&2',
    "    fi",
    "  done",
    "else",
    '  echo "Aborted. No branches deleted."',
    "fi",
    "```",
    "",
    "## Composability",
    "",
    "The procedure (`.claude/procedures/clean-merged-branches.sh`) is",
    "analysis-only and safe to invoke from any agent. The skill is the",
    "interactive entry point — use it when a human is at the keyboard.",
    "Background workers (orchestrator, maintenance-audit) should call",
    "the procedure directly and report its output without acting on it.",
  ].join("\n"),
  referenceFiles: [
    {
      path: "evals/evals.json",
      content: JSON.stringify(
        {
          skill_name: "clean-merged-branches",
          evals: [
            {
              id: 1,
              prompt: "/clean-merged-branches",
              expected_output:
                "The skill runs the analysis-only `clean-merged-branches.sh` procedure against the current checkout's base branch (default: `main`). Each local branch other than HEAD and `main` is reported on one stable line as `MERGED`, `UNMERGED`, `EMPTY`, or `SKIP_WORKTREE` with a file count. When at least one branch classifies as `MERGED` or `EMPTY`, the skill prints the exact deletion list and prompts `Force-delete all <n> with git branch -D? [y/N]`. On explicit `y` / `Y` it force-deletes each branch with `git branch -D` and emits one `DELETED <branch>` line per success. Empty input or any other answer aborts with `Aborted. No branches deleted.` and no branches are deleted. When zero branches are deletable, the skill exits cleanly without prompting.",
              files: [],
              product_context_refs: [],
            },
            {
              id: 2,
              prompt: "/clean-merged-branches --base develop",
              expected_output:
                "The skill forwards `--base develop` to the procedure, which compares every local branch (other than HEAD and `develop`) against `develop` instead of `main`. Branches whose content matches `develop` classify as `MERGED`; others as `UNMERGED`. The base-branch override is also reflected in the deletion prompt (`...with git branch -D?`), and the skill never deletes `develop` itself or the current HEAD. If `develop` does not exist as a local branch, the procedure exits non-zero with a clear diagnostic and the skill aborts without prompting.",
              files: [],
              product_context_refs: [],
            },
            {
              id: 3,
              prompt:
                "/clean-merged-branches — I have a branch called feat/old-feature that I checked out in a sibling worktree under /tmp/work. Confirm it's skipped.",
              expected_output:
                "The procedure detects `feat/old-feature` via `git worktree list --porcelain` and reports `SKIP_WORKTREE feat/old-feature  worktree=/tmp/work` instead of classifying it. The branch is excluded from the deletion list shown at the confirmation prompt. Even if the user answers `y`, the skill never runs `git branch -D feat/old-feature` because the branch is not in the mergeable list.",
              files: [],
              product_context_refs: [],
            },
          ],
        },
        null,
        2,
      ),
    },
  ],
};

/**
 * Renders step 2 ("run the build") of the `pr-workflow` rule.
 *
 * The step is policy-aware on two axes:
 *
 *   - **Scope.** A full `pnpm build:all` is mandated only for changes
 *     that touch build inputs. Documentation-only diffs skip it — CI
 *     is the authoritative content gate, and mandating a slow build
 *     for a markdown edit is what pushed autonomous workers into the
 *     background-build-then-end-turn failure mode below.
 *   - **Remote cache.** The AWS-authentication sentence is emitted
 *     only when the project actually configures a Turborepo remote
 *     cache, and names that project's own profile rather than a
 *     hard-coded one.
 *
 * @see ResolvedBuildPolicy
 */
function renderPrWorkflowBuildStep(
  policy: ResolvedBuildPolicy,
): ReadonlyArray<string> {
  const lines: Array<string> = [
    "2. **Run the build — scoped to what changed.**",
    "",
    "   - **Documentation-only changes** — a diff confined to documentation content (markdown pages, no source, config, or projen inputs) does **not** require `pnpm build:all`. There is nothing to compile, and CI is the authoritative content gate. Commit the content and proceed to the next step.",
    "   - **Everything else** — when the change touches build inputs (source code, `.projenrc.ts`, `projenrc/**`, or any other projen-generated config), run `pnpm build:all` to compile, lint, and test all packages (mirrors the CI pipeline). If the build produces changes to turbo inputs (typically snapshot files or ESLint auto-fixes), commit those changes and run `pnpm build:all` again — the build must complete cleanly with no uncommitted changes.",
  ];

  /**
   * Only assert the credential requirement when it is real. With no
   * remote cache configured, `pnpm build:all` reads and writes a local
   * cache directory only and needs no AWS credentials — emitting the
   * sentence anyway sends agents chasing an authentication step that
   * does not exist.
   */
  if (policy.remoteCacheEnabled && policy.awsProfileName) {
    lines.push(
      "",
      `   \`pnpm build:all\` uses the Turborepo **remote cache**, which requires the user to be authenticated to AWS on the \`${policy.awsProfileName}\` profile. If the command fails due to AWS credentials, ask the user to authenticate first.`,
    );
  }

  lines.push(
    "",
    "   **Never background a long build and end the turn.** If you run a build, run it in the **foreground** to completion before committing, pushing, or opening the PR. Launching `pnpm build:all` as a background process and ending your turn while it is still running strands the deliverable uncommitted — that work is lost. This is the documented root cause of autonomous worker-stall incidents.",
  );

  return lines;
}

/**
 * Builds the GitHub workflow bundle — auto-detected when the project
 * has a GitHub component.
 *
 * The `build` policy conditions the PR-workflow build guidance on the
 * consuming project's Turborepo remote-cache configuration. When
 * omitted, the bundle ships the zero-remote-cache defaults
 * ({@link DEFAULT_BUILD_POLICY}), which emit no AWS-authentication
 * guidance at all.
 */
export function buildGithubWorkflowBundle(
  buildPolicy: ResolvedBuildPolicy = DEFAULT_BUILD_POLICY,
): AgentRuleBundle {
  return {
    name: "github-workflow",
    description: "GitHub issue and PR workflow automation patterns",
    appliesWhen: (project) => hasComponent(project, GitHub),
    rules: [
      {
        name: "issue-workflow",
        description: "Automated workflow for starting work on a GitHub issue",
        scope: AGENT_RULE_SCOPE.ALWAYS,
        content: [
          "# Issue Workflow",
          "",
          '## "Work on issue X" Automation',
          "",
          "When the user says **work on issue X** (or similar), invoke the `issue-worker` agent in interactive mode, passing the issue number in the prompt. Do not perform the branch creation, issue fetching, or planning steps yourself — the agent handles the full workflow (claim, branch, plan, implement, PR) and will pause for your approval at the appropriate checkpoints.",
        ].join("\n"),
        tags: ["workflow"],
      },
      {
        name: "create-issue-workflow",
        description: "Automated workflow for creating a new GitHub issue",
        // ALWAYS scope: users invoke "create an issue" from any
        // context, not only when editing agent / skill / bundle source.
        // Consumers that want to narrow the load can override via
        // `agentConfig.additionalRulePaths` or `excludeRules`.
        scope: AGENT_RULE_SCOPE.ALWAYS,
        content: [
          "# Create Issue Workflow",
          "",
          '## "Create an issue" Automation',
          "",
          "When the user says **create an issue** (or similar), follow these steps exactly:",
          "",
          "1. **Determine the issue type prefix** from the user's description:",
          "   - `epic:` — Large initiatives spanning multiple child issues",
          "   - `feat:` — New features or functionality",
          "   - `fix:` — Bug fixes",
          "   - `chore:` — Maintenance: deps, tooling, config",
          "   - `docs:` — Documentation-only work",
          "   - `refactor:` — Code restructure, no behavior change",
          "   - `release:` — Release preparation, version bumps",
          "   - `hotfix:` — Urgent production fixes",
          "   - If unclear, ask the user which type applies",
          "2. **Compose the issue title** in the format: `<type>: <short description>`",
          "3. **Determine the GitHub issue type** based on the prefix:",
          ...renderTitlePrefixTypeBullets(),
          "4. **Identify prerequisite issues** — if the user mentions dependencies or blockers, include a **Dependencies** section in the body with `Depends on: #<issue-number>`",
          "5. **Determine labels** — every issue must be created with the following labels:",
          "   - **`type:*`** — derived from the issue title prefix:",
          "     - `epic:` → `type:feat`",
          "     - `feat:` → `type:feat`",
          "     - `fix:` → `type:fix`",
          "     - `chore:` → `type:chore`",
          "     - `docs:` → `type:docs`",
          "     - `refactor:` → `type:refactor`",
          "     - `release:` → `type:release`",
          "     - `hotfix:` → `type:hotfix`",
          '   - **`priority:*`** — infer from the user\'s description when possible (e.g., "urgent"/"critical" → `priority:critical`, "important" → `priority:high`, "minor"/"low priority" → `priority:low`). If the priority is unclear, ask the user before creating the issue. Valid values: `priority:critical`, `priority:high`, `priority:medium`, `priority:low`, `priority:trivial`',
          "   - **`status:ready`** — always add unless the issue has dependencies or blockers, in which case use `status:blocked`",
          "6. **Create the issue** using `gh issue create`:",
          "   - `--title '<type>: <description>'`",
          "   - `--body '<issue body>'`",
          "   - `--label '<type-label>' --label '<priority-label>' --label '<status-label>'`",
          "7. **Set the GitHub issue type** by invoking the `set-issue-type.sh` helper (shipped with this bundle):",
          "",
          "   ```sh",
          "   .claude/procedures/set-issue-type.sh <issue-number> <Feature|Task|Epic|Bug>",
          "   ```",
          "",
          "   The helper resolves owner/repo, looks up the issue type node ID, looks up the issue node ID, and applies the `updateIssueIssueType` mutation in one step. It exits non-zero with a clear diagnostic on any error and lists available types if the type name is not recognised.",
          "",
          "   **Under the hood** (documented fallback if the helper is unavailable): the helper performs a two-step GraphQL flow — first a `repository(...).issueTypes` query to map the human-readable type name to a node ID, then the `updateIssueIssueType` mutation. The canonical queries are:",
          "",
          "   ```sh",
          "   gh api graphql -f query='query($owner:String!,$repo:String!){repository(owner:$owner,name:$repo){issueTypes(first:50){nodes{id name}}}}' -f owner=<owner> -f repo=<repo>",
          "   gh api graphql -f query='mutation($issueId:ID!,$typeId:ID!){updateIssueIssueType(input:{issueId:$issueId,issueTypeId:$typeId}){issue{number issueType{name}}}}' -f issueId=<issue-node-id> -f typeId=<issue-type-node-id>",
          "   ```",
          "",
          "### Issue Body Template",
          "",
          "```markdown",
          "## Summary",
          "",
          "<1-3 sentences describing the issue>",
          "",
          "## Details",
          "",
          "<Detailed description, acceptance criteria, or reproduction steps as appropriate>",
          "",
          "## Dependencies",
          "",
          "Depends on: #<issue-number> (if any, otherwise omit this section)",
          "```",
          "",
          "### Important",
          "",
          "- Always use the conventional prefix in the issue title",
          "- Always assign the correct GitHub issue type via the `set-issue-type.sh` helper (step 7) — never via `gh issue create --type`",
          "- Always include `type:*`, `priority:*`, and `status:*` labels",
          "- If the user does not specify a type, ask before creating the issue",
          "- If the priority cannot be inferred from the description, ask the user before creating the issue",
          "- Keep titles concise and descriptive",
        ].join("\n"),
        tags: ["workflow"],
      },
      {
        name: "pr-workflow",
        description: "Automated workflow for opening a pull request",
        scope: AGENT_RULE_SCOPE.ALWAYS,
        content: [
          "# PR Workflow",
          "",
          '## "Open a PR" Automation',
          "",
          "When the user says **open a PR** (or similar), follow these steps exactly:",
          "",
          "1. **Regenerate project files** — run the three-step regen sequence (`pnpm i`, then `pnpm exec projen`, then `pnpm i` again) to ensure all generated files are up to date. The leading `pnpm i` is required because `pnpm exec projen` synthesises against whatever version of configulator (and projen, and any projen plugins) is currently resolved in `node_modules`; if the lockfile has moved past `node_modules` (typically right after `git pull` lands a dependency upgrade, or on a fresh checkout), synth runs against stale templates and produces phantom drift in `.claude/`, `.github/labels.yml`, `CLAUDE.md`, and other generated files. The trailing `pnpm i` picks up any dependency changes projen wrote into `package.json` during synth. Check `git diff` after the third step — if there are changes, commit them before proceeding.",
          ...renderPrWorkflowBuildStep(buildPolicy),
          "3. **Check for uncommitted changes** — if any exist, commit them with a conventional commit message",
          "4. **Pull and rebase from the default branch** — run `git pull origin {{repository.defaultBranch}} --rebase` to incorporate the latest changes and resolve any conflicts before pushing",
          "5. **Push the branch** to origin: `git push -u origin <branch>`",
          "6. **Create the PR** using `gh pr create`:",
          "   - **Title**: use a conventional commit style title (e.g., `feat(scope): short description`)",
          "   - **Body**: include one `Closes #<issue-number>` line per issue the PR closes (derived from the branch name and any additional issues in scope) and a brief summary of changes. See **Closing Keywords** below — never list several issues after a single keyword.",
          "7. **Verify the closing keywords** with `.claude/procedures/check-closing-keywords.sh --body-file <path>` on the body you are about to post, then `.claude/procedures/check-closing-keywords.sh <pr-number>` on the body that landed. Exit `0` means every referenced issue will auto-close; exit `1` prints a `STRANDED #<n>` line per issue GitHub would silently leave open — fix the body with `gh pr edit <pr-number> --body-file <path>` and re-run until it exits `0`.",
          "8. **Delegate review and merge to the `pr-reviewer` sub-agent.** After the PR is created, invoke the `/review-pr <pr-number>` skill (or otherwise hand the new PR number to the `pr-reviewer` sub-agent). The reviewer verifies the diff against the linked issue's acceptance criteria and enables squash auto-merge when all checks pass. Do **not** run `gh pr merge --auto` yourself — review/merge policy lives solely in the `pr-reviewer` agent.",
          "",
          "### Closing Keywords",
          "",
          "**GitHub honours exactly ONE issue reference per closing keyword.** `Closes #593, #594, #595` closes **#593 only** — #594 and #595 stay open forever with no warning anywhere. `Closes #821 / #837` and `Closes #821 and #837` strand the second issue the same way. Give every issue its own keyword, one per line:",
          "",
          "```markdown",
          "Closes #593        <- correct: repeat the keyword, one issue each",
          "Closes #594",
          "",
          "Closes #593, #594  <- WRONG: closes #593, strands #594",
          "Closes #821 / #837 <- WRONG: closes #821, strands #837",
          "Closes #821 and #837  <- WRONG: closes #821, strands #837",
          "```",
          "",
          "`Fixes` and `Resolves` behave identically — the rule is the keyword-to-issue ratio, not the verb. Non-closing references (`Refs #849`, `Related to #607`) are unaffected. `.claude/procedures/check-closing-keywords.sh` is the mechanical check; it skips fenced code blocks, blockquotes, and inline code spans, so a body that *discusses* the malformed form does not trip it.",
          "",
          "### PR Body Template",
          "",
          "```markdown",
          "## Summary",
          "",
          "<1-3 bullet points describing what changed and why>",
          "",
          "Closes #<issue-number>",
          "",
          "## Test Plan",
          "",
          "- [ ] Tests pass locally",
          "- [ ] Relevant changes have been reviewed",
          "```",
          "",
          "When the PR closes more than one issue, the closing block expands to one keyword per issue — it never becomes a list:",
          "",
          "```markdown",
          "Closes #<first-issue-number>",
          "Closes #<second-issue-number>",
          "```",
          "",
          "### Important",
          "",
          "- Always derive the issue number from the branch name (e.g., `feat/42-add-login` → `#42`)",
          "- Use conventional commit format for the PR title",
          "- **One closing keyword per issue, each on its own line.** Never `Closes #A, #B` — GitHub closes only `#A`. Run `.claude/procedures/check-closing-keywords.sh` before and after `gh pr create`; a non-zero exit means the PR would strand an issue",
          "- Delegate merge to the `pr-reviewer` sub-agent — do not merge manually and do not enable auto-merge directly",
        ].join("\n"),
        tags: ["workflow"],
      },
      {
        name: "branch-cleanup",
        description:
          "Local-branch hygiene helpers shipped with the github-workflow bundle, including the /clean-merged-branches skill for safely force-deleting branches whose content has already merged into the base (handles squash merges).",
        scope: AGENT_RULE_SCOPE.ALWAYS,
        content: [
          "# Branch Cleanup",
          "",
          "Local branches accumulate after every merged PR. In squash-merge",
          "repositories `git branch -d` refuses to delete them because the",
          "commit hash on the base differs, even when the branch content is",
          "fully merged. The `github-workflow` bundle ships two affordances",
          "that use content-equality (not commit-graph reachability) to",
          "identify branches safe to force-delete:",
          "",
          "- `/clean-merged-branches` — interactive slash-command skill that",
          "  classifies every local branch, prompts for confirmation, then",
          "  runs `git branch -D` on the confirmed list. See",
          "  `.claude/skills/clean-merged-branches/SKILL.md` for usage,",
          "  output format, and the squash-merge verification algorithm.",
          "- `.claude/procedures/clean-merged-branches.sh` — analysis-only",
          "  procedure for non-interactive agent use (orchestrator,",
          "  maintenance-audit). NEVER deletes — only reports `MERGED` /",
          "  `UNMERGED` / `EMPTY` / `SKIP_WORKTREE` lines.",
        ].join("\n"),
        tags: ["workflow"],
      },
    ],
    skills: [cleanMergedBranchesSkill],
    procedures: [
      setIssueTypeProcedure,
      cleanMergedBranchesProcedure,
      checkClosingKeywordsProcedure,
    ],
  };
}

/**
 * `github-workflow` bundle built with the default (no remote cache)
 * build policy. Preserved for backward compatibility with tests and
 * consumers that import the const directly. Prefer
 * `buildGithubWorkflowBundle(buildPolicy)` when the consuming
 * project's Turborepo configuration is in scope.
 */
export const githubWorkflowBundle: AgentRuleBundle =
  buildGithubWorkflowBundle();
